import React, { useRef, useState } from 'react'
import { Row } from 'react-table'
import { useGetSoldVehiclePath } from '../../../api/dealership/inventory/useGetSoldVehiclePath'
import classNames from 'classnames'
import { ReactComponent as IconUserPath } from '../../../images/performance/icon_user_path.svg'
import { ReactComponent as PerformanceCPUserPathStart } from '../../../images/performance/performance-cp-user-start.svg'
import { ReactComponent as PerformanceCPLineTo } from '../../../images/performance/performance-cp-line-to.svg'
import SoldVehiclePathStep from '../../Inventory/SoldVehiclePathStep'

const SoldVehiclePath = ({
  row,
  filters,
}: {
  row: { original: { vin_number: string } }
  filters?: Record<string, unknown>
}) => {
  const {
    data: vehiclePath,
    isLoading: isLoadingVehiclePath,
    isPreviousData: isPreviousVehiclePath,
  } = useGetSoldVehiclePath({
    filters,
    vin: row.original.vin_number,
  })

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0))
    setScrollLeft(scrollContainerRef.current?.scrollLeft || 0)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    e.preventDefault()

    const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0)
    const walk = (x - startX) * 1
    if (scrollContainerRef.current)
      scrollContainerRef.current.scrollLeft = scrollLeft - walk
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseLeave = () => {
    setIsDragging(false)
  }

  return (
    <div
      key={row.original.vin_number}
      className={classNames(
        'bg-primary-persian-blue/10 p-5 rounded-md flex flex-col gap-5',
        {
          'opacity-50': isPreviousVehiclePath || isLoadingVehiclePath,
        },
      )}>
      <div className={'flex flex-row gap-2 items-center'}>
        <IconUserPath />
        <h3 className={'font-bold text-base'}>Vehicle timeline</h3>
      </div>
      <div className={'flex flex-row items-center justify-center gap-8'}>
        <div
          ref={scrollContainerRef}
          className={classNames(
            'flex items-center overflow-x-scroll w-screen pr-[1000px] pb-4',
            {
              'cursor-grabbing': isDragging,
              'cursor-grab': !isDragging,
            },
          )}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{ userSelect: 'none' }}>
          <div
            className={
              'bg-white border-2 border-primary-xiketic rounded-2xl p-5 items-center flex flex-col justify-center gap-3 min-w-fit'
            }>
            <PerformanceCPUserPathStart />
            <div className={'mx-auto text-center'}>
              <h3 className={'text-primary-xiketic font-bold text-sm'}>Vehicle</h3>
            </div>
          </div>
          {vehiclePath?.map((path, index, array) => {
            return (
              <div key={index} className={'flex items-center'}>
                <div className={'-ml-0.5 -mr-2'}>
                  <PerformanceCPLineTo
                    className={index % 2 === 0 ? '-scale-y-100' : ''}
                  />
                </div>
                {index < array.length && <SoldVehiclePathStep path={path} />}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default SoldVehiclePath
